import { useEffect, useState } from 'react';

import { Button, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GET_USER_PATH, ZOOM_LOGIN_REDIRECT_PATH } from '../config/apiPaths';
import logo from '../images/class-logo-full-color-rgb-313px@72ppi.png';
import { type GetUser } from '../types/apiTypes';
import { ensureUserUuid } from '../utils/ensureUserUuid';
import { getUrls, parseUrlState } from '../utils/url';

const StyledHeader = styled.div`
    margin: 10px;
    display: flex;
    align-items: flex-end;
`;

const StyledLogo = styled.img`
    width: 151px;
`;

const StyledTitle = styled(Typography)`
    margin-left: 26px;

    font-family: Lato;
    font-size: 20px;
    font-style: normal;
    font-weight: bold;
    letter-spacing: 0.15000000596046448px;
    text-align: left;
`;

const StyledMainDiv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const StyledTextDiv = styled.div`
    flex: 1;
    margin-top: 40px;
`;

const InfoTextGroupDiv = styled.div`
    text-align: center;
    margin: auto;
`;

const StyledEmailText = styled(Typography)`
    font-weight: 500;
`;

const StyledEmailsDiv = styled.div`
    margin: 20px auto 0px auto;
    background-color: ${(props) => props.theme.colors.gray};
    border-radius: 8px;
    padding: 9px 26px;
`;

const StyledIncorrectDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const StyledStepText = styled(Typography)`
    margin-top: 50px;
`;

const StyledStepButton = styled(Button)`
    margin-top: 20px;
`;

const ZoomAuthSuccess = () => {
    const { ready, t } = useTranslation('zoom_auth');
    const { api } = getUrls();
    const search = new URLSearchParams(window.location.search);
    const title = search.get('title');
    const state = search.get('state') || '{}';
    const { email } = parseUrlState(state);
    const userUuid = ensureUserUuid();
    const [zoomUser, setZoomUser] = useState<GetUser | null>(null);
    const [openLoginError, setOpenLoginError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const openSignoutTab = () => {
        window.open(`https://${process.env.REACT_ZOOM_WEB_ENDPOINT}/logout`, '_blank');
    };

    const redirectToZoomLogin = () => {
        const redirectUrl = `${api}${ZOOM_LOGIN_REDIRECT_PATH}?state=${state}&output=embed`;
        window.location.href = encodeURI(redirectUrl);
    };

    useEffect(() => {
        const getZoomUser = async () => {
            const { data } = await axios.get<GetUser>(`${api}${GET_USER_PATH}`, { params: { user_uuid: userUuid } });
            if (data.data?.email !== email) {
                setOpenLoginError(true);
            }

            setZoomUser(data);
            setLoading(false);
        };

        getZoomUser();
    }, [setZoomUser, setOpenLoginError, setLoading, email, api, userUuid]);

    const Header = () => (
        <StyledHeader>
            <StyledLogo src={logo}/>
            <StyledTitle>
                {title}
            </StyledTitle>
        </StyledHeader>
    );

    const CorrectEmail = () => (
        <StyledMainDiv>
            <StyledTextDiv>
                <Typography variant='h1'>{t('Success!')}</Typography>
            </StyledTextDiv>
            <Typography>{t('return-to-lms')}</Typography>
        </StyledMainDiv>
    );

    const IncorrectEmail = () => (
        <StyledIncorrectDiv>
            <InfoTextGroupDiv>
                <Typography variant='h1' align='center'>{t('unable-to-login')}</Typography>
                <Typography align='center'>{t('email-issue-error')}</Typography>
            </InfoTextGroupDiv>
            <StyledEmailsDiv>
                <StyledEmailText>{`${t('lms-email')}: ${email}`}</StyledEmailText>
                <StyledEmailText >{`${t('zoom-email')}: ${zoomUser?.data?.email}`}</StyledEmailText>
            </StyledEmailsDiv>
            <StyledStepText align='center'>{t('incorrect-email-step-1')}</StyledStepText>
            <StyledStepButton variant='contained' onClick={openSignoutTab}>{t('sign-out-of-zoom')}</StyledStepButton>
            <StyledStepText align='center'>{t('incorrect-email-step-2')}</StyledStepText>
            <StyledStepButton variant='contained' onClick={redirectToZoomLogin}>{t('try-again')}</StyledStepButton>
        </StyledIncorrectDiv>
    );

    const renderSuccess = () => (openLoginError ? <IncorrectEmail/> : <CorrectEmail/>);

    if (ready) {
        return (
            <>
                <Header/>
                {loading ? <CircularProgress/> : renderSuccess()}
            </>
        );
    }

    return null;
};

export default ZoomAuthSuccess;
